import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addDays, isAfter, isSameDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useProperty } from "../../components/property-provider";
import { useConfig } from "../../lib/config";
import { LOCAL_STORAGE_KEYS } from "../witadmin/Common/constants";
import { ActionsBar } from "./actionsBar";
import { DateStrip } from "./dateStrip";
import { formatDefaultDate, formatVisualDate } from "./helpers/dates";
import {
  useBundles,
  useComplementStaticData,
  useCurrentInventoryState,
  useDiscountStaticData,
  useExtraStaticData,
  useInventoryConfiguration,
  useInventoryLines,
} from "./hooks";
import { Inventories } from "./inventories";
import { messages } from "./messages";
import { CustomModal } from "../../components/modal";
import {
  fetchBackofficeARI,
  processARIData,
  updateARIValues,
  updateSingleARIValue,
} from "./services/ari";
import {
  getFilteredBundleInventories,
  getFilteredExtras,
  getFilteredInventories,
  getFilteredPromotions,
} from "./services/filter";
import { CalendarIcon } from "./shared/assets/CalendarIcon";
import { NAME_TYPE } from "./shared/constants";
import { ARIData } from "./shared/domain/ARIData";
import { BundleInventoryData, hasSomeOwnConfig } from "./shared/domain/BundleInventoryData";
import { ComplementData } from "./shared/domain/ComplementData";
import { ExtraData } from "./shared/domain/ExtraData";
import { InventoryData } from "./shared/domain/InventoryData";
import { PromotionData } from "./shared/domain/PromotionData";
import { NoData } from "./shared/noData";
import { CustomToast } from "./shared/toast";
import { HandleInputChangeRequest, UpdatedARIValue, ValueTypes } from "./shared/types";
import { SortAndFilter } from "./sortAndFilter";
import { useSortAndFilter } from "./sortAndFilter/contexts/SortAndFilterContext";
import {
  BulkUpdateButton,
  ButtonsContainer,
  CalendarContainer,
  CalendarIconContainer,
  ChangeDateButton,
  Container,
  Loader,
} from "./styles";
import { TimeRange } from "./timeRange";
import { AriType, UpdateForm } from "./updateForm";
import { Complements } from "./complements";

interface QuickUpdateData {
  tickers?: string[];
  valueType?: ValueTypes;
  type?: AriType;
  date?: Date;
}

const getInitialDate = (): Date => {
  const localStorageDate = localStorage.getItem(
    LOCAL_STORAGE_KEYS.INVENTORY_MANAGEMENT_TIME_RANGE_FROM
  );
  if (
    localStorageDate &&
    (isSameDay(new Date(localStorageDate), new Date()) ||
      isAfter(new Date(localStorageDate), new Date()))
  ) {
    return new Date(localStorageDate);
  }

  return new Date();
};

export const DEFAULT_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
export const DEFAULT_NUMBER_OF_DAYS = 14;

export const ARIManagement = ({ initialDate }: { initialDate?: Date }): JSX.Element => {
  const propertyConfiguration = usePropertyConfiguration();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<Date>(initialDate ? initialDate : getInitialDate());
  const [timeRangeModalOpen, setTimeRangeModalOpen] = useState<boolean>(false);
  const [nameType, setNameType] = useState<NAME_TYPE>(NAME_TYPE.SHORT);
  const [enableEdition, setEnableEdition] = useState<boolean>(false);
  const [quickUpdateData, setQuickUpdateData] = useState<QuickUpdateData | undefined>(undefined);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState<boolean>(false);
  const [toastType, setToastType] = useState<"success" | "error" | "updateError" | undefined>();
  const [updatedARIValues, setUpdatedARIValues] = useState<UpdatedARIValue>({});
  const [{ inventories, promos, extras, complements, bundleInventories }, setARIData] = useState<
    ARIData
  >({});
  const intl = useIntl();
  const config = useConfig();
  const property = useProperty();
  const inventoryLines = useInventoryLines();
  const bundles = useBundles();
  const currentInventoryState = useCurrentInventoryState();
  const discountStaticData = useDiscountStaticData();
  const extraStaticData = useExtraStaticData();
  const complementStaticData = useComplementStaticData();
  const { accommodations, conditions, mealPlans, occupancies } = useInventoryConfiguration();
  const filters = useSortAndFilter();
  const keycloakAuth = useKeycloakAuthz();

  const filteredInventories = useMemo(() => {
    if (!inventories) {
      return [];
    }
    return getFilteredInventories(inventories, nameType, filters.allValues);
  }, [inventories, nameType, filters]);

  const filteredPromotions = useMemo(() => {
    if (!promos) {
      return [];
    }
    return getFilteredPromotions(promos, filters.allValues);
  }, [promos, filters]);

  const filteredExtras = useMemo(() => {
    if (!extras) {
      return [];
    }
    return getFilteredExtras(extras, filters.allValues);
  }, [extras, filters]);

  const filteredBundleInventories = useMemo(() => {
    if (!bundleInventories) {
      return [];
    }
    return getFilteredBundleInventories(bundleInventories, nameType, filters.allValues);
  }, [bundleInventories, nameType, filters]);

  const showAltStyles = useMemo(
    () =>
      Boolean(
        propertyConfiguration &&
          (typeof propertyConfiguration.showAltARIManagerStyles === "undefined" ||
            propertyConfiguration.showAltARIManagerStyles === "1")
      ),
    [propertyConfiguration]
  );

  const numberOfDays = useMemo(() => {
    if (!propertyConfiguration) return;
    if (!showAltStyles || !propertyConfiguration.numberOfDaysARIManager)
      return DEFAULT_NUMBER_OF_DAYS;
    return Number(propertyConfiguration.numberOfDaysARIManager);
  }, [propertyConfiguration, showAltStyles]);

  useEffect(() => {
    if (
      !isLoading ||
      !property ||
      !config ||
      !inventoryLines ||
      !currentInventoryState ||
      !discountStaticData ||
      !extraStaticData ||
      !complementStaticData ||
      !bundles ||
      !numberOfDays
    ) {
      return;
    }

    const startDate = formatDefaultDate(fromDate);
    const endDate = formatDefaultDate(addDays(new Date(fromDate), numberOfDays - 1));
    fetchBackofficeARI(property?.ticker, startDate, endDate, config?.ariServiceUrl)
      .then(res => {
        const ariData = processARIData(
          startDate,
          endDate,
          ARIData.of(
            InventoryData.of(res.data, inventoryLines, currentInventoryState),
            PromotionData.of(res.data, discountStaticData, startDate, endDate),
            ExtraData.of(res.data, extraStaticData, startDate, endDate),
            ComplementData.of(res.data, complementStaticData, startDate, endDate),
            BundleInventoryData.of(res.data, bundles, inventoryLines, startDate, endDate)
          )
        );

        setARIData(ariData);
        setLoading(false);
      })
      .catch(err => {
        console.error("error fetching ARI data", err);
        setToastType("error");
      });
  }, [
    property,
    config,
    fromDate,
    inventoryLines,
    currentInventoryState,
    discountStaticData,
    extraStaticData,
    complementStaticData,
    isLoading,
    bundles,
    numberOfDays,
  ]);

  useEffect(() => {
    if (filteredInventories && filteredPromotions && filteredExtras && complements) {
      setLoading(false);
      return;
    }
    setLoading(true);
  }, [filteredInventories, filteredPromotions, filteredExtras, complements]);

  const inventoryTickers = useMemo(
    () =>
      inventories
        ?.filter(it => !it.isChild)
        .map(it => ({
          ticker: it.ticker,
          name: it.name,
          accommodation: it.configuration.accommodation,
          condition: it.configuration.condition,
          occupancy: it.configuration.occupancy,
          mealPlan: it.configuration.mealPlan,
          valueTypes: Object.keys(it.dailyValues).filter(
            key => !it.dailyValues[key as ValueTypes]?.parentTicker
          ) as ValueTypes[],
        })),
    [inventories]
  );

  const extraTickers = useMemo(
    () =>
      extras?.map(it => ({
        ticker: it.ticker,
        valueTypes: Object.keys(it.dailyValues).filter(
          key => !it.dailyValues[key as "rate" | "closed"]?.parentTicker
        ) as ValueTypes[],
      })),
    [extras]
  );

  const promoTickers = useMemo(
    () =>
      promos?.map(it => ({
        ticker: it.ticker,
        valueTypes: [ValueTypes.closed],
      })),
    [promos]
  );

  const complementTickers = useMemo(
    () =>
      complements?.map(it => ({
        ticker: it.ticker,
        valueTypes: [ValueTypes.rate],
      })),
    [complements]
  );

  const bundleTickers = useMemo(
    () =>
      bundleInventories
        ?.filter(inv => {
          const bundle = bundles?.find(bundle => inv.ticker === bundle.ticker);
          return bundle && hasSomeOwnConfig(bundle);
        })
        .map(it => ({
          ticker: it.ticker,
          name: it.name,
          accommodation: it.configuration.accommodation,
          condition: it.configuration.condition,
          occupancy: it.configuration.occupancy,
          mealPlan: it.configuration.mealPlan,
          valueTypes: Object.keys(it.dailyValues).filter(
            key => !it.dailyValues[key]?.parentTicker && !it.dailyValues[key]?.parentTickers
          ) as ValueTypes[],
        })),
    [bundleInventories, bundles]
  );

  const hasData = useMemo(
    () =>
      (filteredInventories && filteredInventories.length > 0) ||
      (filteredPromotions && filteredPromotions.length > 0) ||
      (filteredExtras && filteredExtras.length > 0),
    [filteredInventories, filteredPromotions, filteredExtras]
  );

  const hasComplements = useMemo(() => !!(complements && complements.length > 0), [complements]);

  const handleChangeFromDate = useCallback((date: Date): void => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.INVENTORY_MANAGEMENT_TIME_RANGE_FROM,
      formatDefaultDate(date)
    );
    setFromDate(date);
    setTimeRangeModalOpen(false);
    setUpdatedARIValues({});
    setLoading(true);
  }, []);

  const handleARIValueChange = useCallback(
    async (request: HandleInputChangeRequest): Promise<void> => {
      if (!request) {
        setToastType("updateError");
        return;
      }
      if (typeof request.value !== "boolean" && (request.value < 0 || isNaN(request.value))) {
        setToastType("updateError");
        return;
      }
      updateSingleARIValue(
        property?.ticker,
        config?.ariServiceUrl,
        request.ticker,
        request.valueType,
        formatDefaultDate(request.date),
        request.value,
        request.type,
        keycloakAuth.rpt
      )
        .then(() => {
          const currentUpdatedData =
            updatedARIValues[request.ticker] && updatedARIValues[request.ticker][request.valueType]
              ? { ...updatedARIValues[request.ticker][request.valueType] }
              : {};
          setUpdatedARIValues({
            ...updatedARIValues,
            [request.ticker]: {
              [request.valueType]: {
                ...currentUpdatedData,
                [formatDefaultDate(request.date)]: request.value,
              },
            },
          });
          setToastType("success");
        })
        .catch(err => {
          console.error("error updating single ARI value", err);
          setToastType("updateError");
        });
    },
    [property?.ticker, config?.ariServiceUrl, keycloakAuth.rpt, updatedARIValues]
  );

  const handleUpdateSubmit = useCallback(
    (
      valueType: ValueTypes,
      operator: string,
      value: number | boolean,
      periods: {
        startDate: Date;
        endDate: Date;
        weekdays: number[];
      }[],
      ariItems: string[],
      type: AriType
    ): void => {
      if (
        !valueType ||
        !operator ||
        typeof value === undefined ||
        !ariItems.length ||
        !periods.length
      ) {
        setQuickUpdateData(undefined);
        setBulkUpdateModalOpen(false);
        setToastType("updateError");
        return;
      }

      setUpdating(true);
      updateARIValues(
        property?.ticker,
        config?.ariServiceUrl,
        valueType,
        operator,
        value,
        periods,
        ariItems,
        type,
        keycloakAuth.rpt
      )
        .then(() => {
          setToastType("success");
          setUpdatedARIValues({});
          setLoading(true);
        })
        .catch(err => {
          console.error("error updating ARI values", err);
          setToastType("updateError");
          setUpdating(false);
        })
        .finally(() => {
          setQuickUpdateData(undefined);
          setBulkUpdateModalOpen(false);
          setUpdating(false);
        });
    },
    [property?.ticker, config?.ariServiceUrl, keycloakAuth.rpt]
  );

  const handleClickCalendarIcon = useCallback(() => {
    if (isSameDay(fromDate, new Date())) {
      return;
    }
    handleChangeFromDate(new Date());
  }, [fromDate, handleChangeFromDate]);

  const showDateStrip = useMemo(() => Boolean(propertyConfiguration), [propertyConfiguration]);

  const handleDayQuickUpdate = useCallback(
    (date: Date) => {
      if (isLoading || !filteredInventories) return;
      const inventoryTickersList =
        filteredInventories
          .filter(it => it.dailyValues.closed && !it.dailyValues.closed.parentTicker)
          .map(it => it.ticker) ?? [];
      setQuickUpdateData({
        tickers: inventoryTickersList,
        date,
        valueType: ValueTypes.closed,
        type: AriType.INVENTORY_LINES,
      });
    },
    [isLoading, filteredInventories]
  );

  return (
    <Container hasMaxWidth={!showAltStyles}>
      <SortAndFilter
        roomTypesOptions={accommodations}
        ratesOptions={conditions}
        mealPlansOptions={mealPlans}
        occupanciesOptions={occupancies}
      />

      <CalendarContainer>
        <ButtonsContainer>
          <ChangeDateButton onClick={() => setTimeRangeModalOpen(true)}>
            {formatVisualDate(fromDate)}
          </ChangeDateButton>

          <BulkUpdateButton onClick={() => setBulkUpdateModalOpen(true)} disabled={isLoading}>
            <FormattedMessage {...messages.bulkUpdate} />
          </BulkUpdateButton>
        </ButtonsContainer>

        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={
            <Tooltip id="calendar-icon">
              <FormattedMessage {...messages.backToCurrentDay} />
            </Tooltip>
          }>
          <CalendarIconContainer onClick={handleClickCalendarIcon}>
            <CalendarIcon />
          </CalendarIconContainer>
        </OverlayTrigger>

        {showDateStrip && numberOfDays && (
          <DateStrip
            startDate={fromDate}
            setStartDate={handleChangeFromDate}
            loading={isLoading}
            numberOfDays={numberOfDays}
            lite={showAltStyles}
            onClickDay={handleDayQuickUpdate}
            showAltStyles={showAltStyles}
          />
        )}
      </CalendarContainer>

      {!isLoading && (
        <>
          {hasData && numberOfDays && (
            <ActionsBar
              handleToggleName={type => setNameType(type)}
              handleEnableEdition={() => setEnableEdition(!enableEdition)}
              nameType={nameType}
              enableEdition={enableEdition}
              numberOfDays={numberOfDays}
            />
          )}

          {!hasData && <NoData />}

          {filteredInventories &&
            filteredPromotions &&
            filteredExtras &&
            filteredBundleInventories &&
            numberOfDays && (
              <Inventories
                items={filteredInventories}
                promotions={filteredPromotions}
                extras={filteredExtras}
                handleQuickUpdate={(ticker, valueType, type) =>
                  setQuickUpdateData({ tickers: [ticker], valueType, type })
                }
                bundleInventories={filteredBundleInventories}
                enableEdition={enableEdition}
                handleInputChange={handleARIValueChange}
                nameType={nameType}
                updatedARIValues={updatedARIValues}
                showAltStyles={showAltStyles}
                numberOfDays={numberOfDays}
              />
            )}

          {hasComplements && !!complements && numberOfDays && (
            <Complements
              items={complements}
              handleQuickUpdate={(ticker, valueType, type) =>
                setQuickUpdateData({ tickers: [ticker], valueType, type })
              }
              enableEdition={enableEdition}
              handleInputChange={handleARIValueChange}
              updatedARIValues={updatedARIValues}
              showAltStyles={showAltStyles}
              numberOfDays={numberOfDays}
            />
          )}
        </>
      )}

      {isLoading && (
        <Loader>
          <FormattedMessage {...messages.loading} />
          <FontAwesomeIcon icon={faSyncAlt} spin />
        </Loader>
      )}

      {timeRangeModalOpen && (
        <CustomModal
          isOpen={timeRangeModalOpen}
          onClose={() => setTimeRangeModalOpen(false)}
          title={intl.formatMessage(messages.timeRange)}>
          <TimeRange date={fromDate} onSubmit={date => handleChangeFromDate(date)} />
        </CustomModal>
      )}

      {bulkUpdateModalOpen && (
        <CustomModal
          isOpen={bulkUpdateModalOpen}
          onClose={() => setBulkUpdateModalOpen(false)}
          title={intl.formatMessage(messages.bulkUpdate)}
          maxWidth="950px">
          <UpdateForm
            periods={[
              {
                startDate: new Date(),
                endDate: new Date(),
                weekdays: DEFAULT_WEEKDAYS,
              },
            ]}
            onSubmit={handleUpdateSubmit}
            onClose={() => setBulkUpdateModalOpen(false)}
            inventoryLines={inventoryTickers}
            complements={complementTickers}
            extras={extraTickers}
            promos={promoTickers}
            bundles={bundleTickers}
            showWeekDays
            showAddPeriod
            showApplyTo
            accommodations={accommodations}
            conditions={conditions}
            mealPlans={mealPlans}
            configurations={occupancies}
            updating={updating}
          />
        </CustomModal>
      )}

      {quickUpdateData && numberOfDays && (
        <CustomModal
          isOpen={!!quickUpdateData}
          onClose={() => setQuickUpdateData(undefined)}
          title={
            quickUpdateData.date
              ? intl.formatMessage(messages.quickUpdateBlock)
              : intl.formatMessage(messages.quickUpdate)
          }
          maxWidth="605px"
          overflowVisible>
          <UpdateForm
            items={quickUpdateData.tickers ?? []}
            periods={[
              {
                startDate: quickUpdateData.date || fromDate,
                endDate: quickUpdateData.date || addDays(fromDate, numberOfDays - 1),
                weekdays: DEFAULT_WEEKDAYS,
              },
            ]}
            valueType={quickUpdateData.valueType}
            onSubmit={handleUpdateSubmit}
            onClose={() => setQuickUpdateData(undefined)}
            accommodations={accommodations}
            conditions={conditions}
            mealPlans={mealPlans}
            configurations={occupancies}
            type={quickUpdateData.type ? quickUpdateData.type : AriType.INVENTORY_LINES}
            updating={updating}
            isSpecificDate={Boolean(quickUpdateData.date)}
          />
        </CustomModal>
      )}
      <CustomToast show={!!toastType} type={toastType} onClose={() => setToastType(undefined)} />
    </Container>
  );
};
